/**
 * URL Generator
 *
 * Purpose: Generate URLs for CFT course pages and external resources
 * Usage: Results display, course recommendations
 * Features: State-specific course links, external resource URLs
 */

import { stateData } from '../data';

/**
 * Base URL for CFT website (update this with actual CFT domain)
 */
const CFT_BASE_URL = 'https://www.cft.edu.au'; 

/**
 * Generate CFT course URL based on requirements
 * @param {object} requirements - Calculated FSS requirements from calculation engine
 * @returns {string} CFT course URL
 */
const generateCourseUrl = (requirements) => {
  const { compliance, training } = requirements;
  
  // Convert state code to lowercase for URL
  const stateParam = compliance.state.toLowerCase();
  
  // Convert industry sector to URL-friendly slug
  // remove unused variable
  // const industrySector = getIndustrySectorSlug(compliance.businessType);
  
  // Determine primary skill set for URL routing
  const skillSetParam = getSkillSetSlug(training.skillSetCode);
  
  // Build the course URL
  // Format: /food-safety-supervisor/{state}/{skillset}
  // Example: /food-safety-supervisor/nsw/hospitality
  const courseUrl = `${CFT_BASE_URL}/food-safety-supervisor/${stateParam}/${skillSetParam}`;
  
  return courseUrl;
};

/**
 * Generate external resource URLs (legislation, governing bodies)
 * @param {string} stateCode - Selected state code
 * @returns {object} Collection of relevant URLs
 */
const generateResourceUrls = (stateCode) => {
  const state = stateData[stateCode];
  
  if (!state) {
    return {
      governingBody: null,
      legislation: null,
      standard322a: null,
      foodStandardsAU: null
    };
  }
  
  return {
    governingBody: state.governingBody.website,
    governingBodyName: state.governingBody.name,
    
    // Food Standards Australia & New Zealand resources
    standard322a: 'https://www.foodstandards.gov.au/sites/default/files/2023-12/Standard%203.2.2A%20Food%20Safety%20Management%20Tools-%20corrected%20271123.pdf',
    foodStandardsAU: 'https://www.foodstandards.gov.au/',
    foodStandardsCode: 'https://www.foodstandards.gov.au/code/Pages/default.aspx',
    
    // State-specific legislation links
    legislation: getStateLegislationUrl(stateCode),
    
    // ASQA (Australian Skills Quality Authority)
    asqa: 'https://www.asqa.gov.au/',
    
    // Australian Qualifications Framework
    aqf: 'https://www.aqf.edu.au/'
  };
};

/**
 * Get state-specific legislation URL
 * @param {string} stateCode - State code
 * @returns {string} Legislation URL
 */
const getStateLegislationUrl = (stateCode) => {
  const legislationUrls = {
    NSW: 'https://legislation.nsw.gov.au/view/html/inforce/current/act-2003-043',
    VIC: 'https://www.legislation.vic.gov.au/in-force/acts/food-act-1984',
    QLD: 'https://www.legislation.qld.gov.au/view/html/inforce/current/act-2006-003',
    WA: 'https://www.legislation.wa.gov.au/legislation/statutes.nsf/law_a767.html',
    SA: 'https://www.legislation.sa.gov.au/LZ/C/A/FOOD%20ACT%202001.aspx',
    TAS: 'https://www.legislation.tas.gov.au/view/html/inforce/current/act-2003-008',
    NT: 'https://legislation.nt.gov.au/en/Legislation/FOOD-ACT-2004',
    ACT: 'https://www.legislation.act.gov.au/a/2001-66'
  };
  
  return legislationUrls[stateCode] || null;
};

/**
 * Generate shareable URL for current calculator state
 * @param {string} stateCode - Selected state code
 * @param {string} businessTypeId - Selected business type ID
 * @returns {string} Shareable URL with query parameters
 */
const generateShareableUrl = (stateCode, businessTypeId) => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '';
  const params = new URLSearchParams({
    state: stateCode,
    business: businessTypeId
  });
  
  return `${baseUrl}?${params.toString()}`;
};

/**
 * Parse shareable URL to extract state and business type
 * @param {string} url - URL to parse (defaults to current URL)
 * @returns {object} Parsed state and business type
 */
const parseShareableUrl = (url = null) => {
  if (typeof window === 'undefined') {
    return { state: null, businessTypeId: null };
  }
  
  const searchParams = new URLSearchParams(url || window.location.search);
  
  return {
    state: searchParams.get('state'),
    businessTypeId: searchParams.get('business')
  };
};

/**
 * Generate recertification course URL
 * @param {string} stateCode - State code
 * @returns {string} Recertification course URL
 */
const generateRecertificationUrl = (stateCode) => {
  const stateParam = stateCode.toLowerCase();
  return `${CFT_BASE_URL}/food-safety-supervisor/recertification/${stateParam}`;
};

/**
 * Generate contact/inquiry URL with pre-filled information
 * @param {object} requirements - Calculated requirements
 * @returns {string} Contact form URL with query parameters
 */
const generateContactUrl = (requirements) => {
  const params = new URLSearchParams({
    subject: 'Food Safety Supervisor Inquiry',
    state: requirements.compliance.state,
    business: requirements.compliance.businessType,
    units: requirements.training.units.map(u => u.code).join(',')
  });
  
  return `${CFT_BASE_URL}/contact?${params.toString()}`;
};

/**
 * Helper: Convert business type to industry sector slug
 * @param {string} businessType - Business type name
 * @returns {string} URL-friendly industry sector slug
 */
const getIndustrySectorSlug = (businessType) => {
  // Map business types to URL slugs
  const slugMap = {
    // Hospitality & Retail
    'Restaurant': 'hospitality',
    'Cafe': 'hospitality',
    'Takeaway': 'hospitality',
    'Catering': 'hospitality',
    'Hotel/Pub': 'hospitality',
    'Bakery': 'hospitality',
    'Mobile Food Vendor': 'hospitality',
    'Supermarket (with deli)': 'retail',
    'Market Stall': 'retail',
    'Service Station (with food)': 'retail',
    'Seafood Retailer': 'retail',
    
    // Health & Community
    'Hospital': 'healthcare',
    'Aged Care Facility': 'aged-care',
    'Childcare Centre': 'childcare',
    'School Canteen': 'schools',
    
    // Food Processing
    'Food Processing Plant': 'food-processing',
    'Brewery/Winery': 'food-processing'
  };
  
  return slugMap[businessType] || 'general';
};

/**
 * Helper: Convert skill set code to URL slug
 * @param {string} skillSetCode - Skill set code
 * @returns {string} URL-friendly skill set slug
 */
const getSkillSetSlug = (skillSetCode) => {
  const slugMap = {
    'SITSS00069': 'hospitality',
    'HLTSS00061': 'health-community',
    'Food Processing specific': 'food-processing'
  };
  
  return slugMap[skillSetCode] || 'general';
};

/**
 * Generate print-friendly URL
 * @param {string} stateCode - State code
 * @param {string} businessTypeId - Business type ID
 * @returns {string} Print version URL
 */
const generatePrintUrl = (stateCode, businessTypeId) => {
  return `${generateShareableUrl(stateCode, businessTypeId)}&print=true`;
};

/**
 * Generate email body with requirements summary
 * @param {object} requirements - Calculated requirements
 * @returns {string} Pre-filled email body text
 */
const generateEmailBody = (requirements) => {
  const { compliance, training } = requirements;
  
  const emailBody = `
Hi,

I used the CFT Food Safety Supervisor Calculator and would like more information about my requirements:

State: ${compliance.stateName}
Business Type: ${compliance.businessType}
FSS Required: ${compliance.fssRequired ? 'Yes' : 'Recommended'}

Required Training Units:
${training.units.map(u => `- ${u.code}: ${u.name}`).join('\\n')}

Total Duration: ${training.totalDuration} hours
Skill Set Code: ${training.skillSetCode}

Please contact me with more information about enrolling in the Food Safety Supervisor course.

Thank you.
  `.trim();
  
  return encodeURIComponent(emailBody);
};

/**
 * Generate mailto link with pre-filled information
 * @param {object} requirements - Calculated requirements
 * @returns {string} Mailto link
 */
const generateMailtoLink = (requirements) => {
  const subject = encodeURIComponent('Food Safety Supervisor Course Inquiry');
  const body = generateEmailBody(requirements);
  const email = 'info@cft.edu.au'; // TODO: Update with actual CFT email
  
  return `mailto:${email}?subject=${subject}&body=${body}`;
};

export {
  generateCourseUrl,
  generateResourceUrls,
  generateShareableUrl,
  parseShareableUrl,
  generateRecertificationUrl,
  generateContactUrl,
  generatePrintUrl,
  generateMailtoLink,
  getStateLegislationUrl,
  CFT_BASE_URL
};

export default {
  generateCourseUrl,
  generateResourceUrls,
  generateShareableUrl,
  parseShareableUrl,
  generateRecertificationUrl,
  generateContactUrl,
  generatePrintUrl,
  generateMailtoLink,
  getStateLegislationUrl
};
